import type {
  StaffImportColumnNote,
  StaffImportMapping,
  StaffImportPreview,
  StaffImportRow,
} from "@lafayette/schemas";

// The source columns as the screens see them. A column is identified by its INDEX (0-based), never by
// its header: two columns may share a header and a header may be blank. The header is untrusted cell
// text: it is shown as text only.

export type ImportColumnView = Readonly<{
  index: number;
  header: string;
  samples: readonly string[];
  notes: readonly StaffImportColumnNote[];
}>;

/** 0 is A, 25 is Z, 26 is AA. The letter a spreadsheet shows above the column. */
export function columnLetter(index: number): string {
  let rest = index;
  let letters = "";
  do {
    letters = String.fromCharCode(65 + (rest % 26)) + letters;
    rest = Math.floor(rest / 26) - 1;
  } while (rest >= 0);
  return letters;
}

/** "Column C · eventName", or "Column F (no header)" for a blank header. */
export function columnLabel(column: Pick<ImportColumnView, "index" | "header">): string {
  const letter = `Column ${columnLetter(column.index)}`;
  const header = column.header.trim();
  return header === "" ? `${letter} (no header)` : `${letter} · ${header}`;
}

const sameHeader = (header: string) => header.normalize("NFKC").trim().toLowerCase();

/** The header notes the browser can work out alone: a blank header and a header another column shares. */
export function headerNotes(
  headers: readonly string[],
): ReadonlyMap<number, readonly StaffImportColumnNote[]> {
  const counts = new Map<string, number>();
  for (const header of headers) {
    const key = sameHeader(header);
    if (key !== "") counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const notes = new Map<number, readonly StaffImportColumnNote[]>();
  headers.forEach((header, index) => {
    const key = sameHeader(header);
    if (key === "") notes.set(index, ["BLANK_HEADER"]);
    else if ((counts.get(key) ?? 0) > 1) notes.set(index, ["DUPLICATE_HEADER"]);
  });
  return notes;
}

function mergeNotes(
  server: readonly StaffImportColumnNote[],
  local: readonly StaffImportColumnNote[] | undefined,
): readonly StaffImportColumnNote[] {
  return [...new Set([...server, ...(local ?? [])])];
}

export function columnsFromPreview(preview: StaffImportPreview): readonly ImportColumnView[] {
  const local = headerNotes(preview.columns.map((column) => column.header));
  return preview.columns.map((column) => ({
    index: column.index,
    header: column.header,
    samples: column.samples,
    notes: mergeNotes(column.notes, local.get(column.index)),
  }));
}

/** The first non-empty values of one column, for the "Sample values" cell. */
export function sampleValues(
  rows: readonly Pick<StaffImportRow, "rawCells">[],
  columnIndex: number,
  count = 3,
): readonly string[] {
  const samples: string[] = [];
  for (const row of rows) {
    const cell = row.rawCells[columnIndex];
    if (cell !== undefined && cell.trim() !== "") samples.push(cell);
    if (samples.length >= count) break;
  }
  return samples;
}

/** The columns of a saved draft. The server keeps the headers; the samples come from the rows read so far. */
export function columnsFromMapping(
  mapping: Pick<StaffImportMapping, "sourceHeaders">,
  rows: readonly Pick<StaffImportRow, "rawCells">[],
): readonly ImportColumnView[] {
  const local = headerNotes(mapping.sourceHeaders);
  return mapping.sourceHeaders.map((header, index) => ({
    index,
    header,
    samples: sampleValues(rows, index),
    notes: local.get(index) ?? [],
  }));
}
